import { useEffect, useMemo, useState } from "react"
import {
  get,
  find,
  concat,
  reject,
  findIndex,
  clone,
  pick,
  merge,
  map,
  includes,
} from "lodash"
import uuid from "uuid/v4"
import { useSelector, ReactReduxContext } from "react-redux"
import { useLocation } from "react-router"
import { switchArray, switchArrayType } from "components/actions/utils"
import APIs from "redux/apis"
import { APIResponse } from "redux/apis/types"
import { PAGE_NUM, STOCK_BENEFIT_CONDITIONS } from "config/const"
import { checkHasSymbol, createRequestBit } from "utils/util"
import { LoadStateType, pageLoadRegist } from "redux/ducks/PageLoad"
import store from "redux/store"

export type RequestParams = {
  keyword?: string
  Markets?: string
  TPSectors?: string
  SPSectors?: string
  Filter?: Array<{
    Type: number
    Param: { From?: number | string; To?: number | string }
  }>
  _skip: number
  _take: number
  _orderBy?: string
}

type StockBenefitRequestParams = {
  _skip: number
  _take: number
  RightFixMonth?: Array<String>
  _where?: string
  NeededAmount?: Array<String>
}

const getRequestParameter = (
  list: Array<number>,
  definition: Array<object>,
  path: string
) => {
  return list.map(id => {
    const targetList = find(definition, { id: id })
    return get(targetList, path, null)
  })
}

// 詳細検索

export type DetailSearchActionType = {
  type:
    | "LOAD_DETAIL"
    | "LOADED_DETAIL"
    | "CHANGE_DETAIL_TOGGLE"
    | "CHANGE_DETAIL_MARKET"
    | "CHANGE_DETAIL_COUNTRY"
    | "ALL_DETAIL_MARKET"
    | "CHANGE_DETAIL_JP_INDUSTRY"
    | "ALL_DETAIL_JP_INDUSTRY"
    | "CHANGE_DETAIL_SP_CATEGORY"
    | "CHANGE_DETAIL_SP_INDUSTRY"
    | "ALL_DETAIL_SP_INDUSTRY"
    | "ADD_COMPARE_SYMBOL"
    | "RESET_COMPARE_SYMBOL"
    | "CHANGE_COMPARE_MODE"
    | "CHANGE_DETAIL_KEYWORD"
    | "CHANGE_DETAIL_PAGE"
    | "LOAD_SYMBOL_DETAIL"
    | "SORT_SYMBOL_DETAIL"
  keyword?: string
  id?: number
  page?: number
  // すべて選択時
  all?: Array<number>
  symbol?: APIResponse.Res
  sort?: string | null
}

export type DetailSearchStateType = {
  load: boolean
  openToggle: Array<number>
  keyword: string
  markets: Array<number>
  jpIndustry: Array<number>
  spIndustry: Array<number>
  compare: Array<APIResponse.Res>
  page: number
  isComparePage: boolean
  sort: string | null
}

export const useDetailSearchReducer = (
  state: DetailSearchStateType,
  action: DetailSearchActionType
) => {
  switch (action.type) {
    case "LOAD_DETAIL":
      return {
        ...state,
        page: 0,
        load: true,
      }
    case "LOADED_DETAIL":
      return {
        ...state,
        load: false,
      }
    case "CHANGE_DETAIL_TOGGLE":
      return {
        ...state,
        openToggle: switchArrayType<number>(state.openToggle, action.id),
      }
    case "CHANGE_DETAIL_MARKET":
      return {
        ...state,
        markets: switchArrayType<number>(state.markets, action.id),
      }
    case "CHANGE_DETAIL_COUNTRY":
      return {
        ...state,
        markets: switchArray<number>(state.markets, action.all),
      }
    case "ALL_DETAIL_MARKET":
      return {
        ...state,
        markets: state.markets.length === 0 ? action.all : [],
      }
    case "CHANGE_DETAIL_JP_INDUSTRY":
      return {
        ...state,
        jpIndustry: switchArrayType<number>(state.jpIndustry, action.id),
      }
    case "ALL_DETAIL_JP_INDUSTRY":
      return {
        ...state,
        jpIndustry: state.jpIndustry.length === 0 ? action.all : [],
      }
    case "CHANGE_DETAIL_SP_CATEGORY":
      return {
        ...state,
        spIndustry: switchArray<number>(state.spIndustry, action.all),
      }
    case "CHANGE_DETAIL_SP_INDUSTRY":
      return {
        ...state,
        spIndustry: switchArrayType<number>(state.spIndustry, action.id),
      }
    case "ALL_DETAIL_SP_INDUSTRY":
      return {
        ...state,
        spIndustry: state.spIndustry.length === 0 ? action.all : [],
      }
    case "ADD_COMPARE_SYMBOL":
      const hasSymbol: number = checkHasSymbol(state.compare, action.symbol)
      //登録を5つまでに
      if (hasSymbol === null && state.compare.length >= 5) return state
      return {
        ...state,
        compare:
          hasSymbol === null
            ? concat(state.compare, action.symbol)
            : reject(state.compare, (d, i) => i === hasSymbol),
      }
    case "RESET_COMPARE_SYMBOL":
      return {
        ...state,
        compare: [],
      }
    case "CHANGE_COMPARE_MODE":
      const canCompare = state.compare.length !== 0
      return {
        ...state,
        isComparePage: canCompare ? !state.isComparePage : false,
        openToggle: canCompare && state.isComparePage ? [] : state.openToggle,
      }
    case "CHANGE_DETAIL_KEYWORD":
      return { ...state, keyword: action.keyword }
    case "CHANGE_DETAIL_PAGE":
      return { ...state, page: action.page, load: true }
    case "LOAD_SYMBOL_DETAIL":
      return {
        ...state,
        load: true,
        openToggle: [2],
        keyword: "",
        markets: [],
        jpIndustry: [],
        spIndustry: [action.id],
        compare: [],
        page: 0,
      }
    case "SORT_SYMBOL_DETAIL":
      return {
        ...state,
        load: true,
        sort: action.sort,
      }
  }
}

type UseDetailSearchResultType = (
  state: DetailSearchStateType,
  cb: Function
) => APIResponse.Res
export const useDetailSearchResult: UseDetailSearchResultType = (state, cb) => {
  const loadingId = useMemo(() => uuid(), [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const [data, setData] = useState<APIResponse.Res>()
  const path = get(useLocation(), "pathname")

  const load = async () => {
    await store.dispatch(pageLoadRegist(loadingId, true))
    const params: RequestParams = {
      _skip: PAGE_NUM.PAGE_20 * state.page,
      _take: PAGE_NUM.PAGE_20,
      _orderBy: state.sort,
    }
    if (state.keyword) params["keyword"] = state.keyword
    if (!!state.markets.length)
      params["Markets"] = createRequestBit(state.markets, 21)
    if (!!state.jpIndustry.length)
      params["TPSectors"] = createRequestBit(state.jpIndustry, 33)
    if (!!state.spIndustry.length)
      params["SPSectors"] = createRequestBit(state.spIndustry, 69)
    const res = await APIs.SymbolDetail.EquitySymbolList(params)
    setData(res)
    await store.dispatch(pageLoadRegist(loadingId, false))
    cb()
  }
  useEffect(() => {
    if (state.load) load()
  }, [state.load])

  useEffect(() => {
    if (isPageLoading && includes(path, "search")) load()
  }, [isPageLoading])
  return data
}

export type DetailCompareResult = Array<APIResponse.Res>
type UseDetailCompareResult = (
  compare: Array<APIResponse.Res>,
  type: boolean
) => DetailCompareResult
export const useDetailCompareResult: UseDetailCompareResult = (
  compare,
  type
) => {
  const [data, setData] = useState<Array<APIResponse.Res>>([])
  const loadingId = useMemo(() => uuid(), [])
  useEffect(() => {
    ;(async () => {
      if (type) {
        store.dispatch(pageLoadRegist(loadingId, true))
        setData([])
        const apis = async (list: object) => {
          let _data = {}
          await Promise.all([
            APIs.SymbolDetail.GetCompanyInfo(
              pick(list, ["Symbol", "SecurityType"])
            ),
            APIs.SymbolDetail.SettledAccountReport(
              merge(pick(list, ["Symbol", "SecurityType"]), { Type: 2 })
            ),
            APIs.SymbolDetail.SettledAccountReport(
              merge(pick(list, ["Symbol", "SecurityType"]), { Type: 3 })
            ),
            APIs.SymbolDetail.SettledAccountReport(
              merge(pick(list, ["Symbol", "SecurityType"]), { Type: 11 })
            ),
          ]).then(res => {
            const GetCompanyInfo = pick(get(res, 0), ["CompanyInfo", "Symbol"])
            const Uriagedaka = map(get(res, [1, "ResultList"]), items => {
              return {
                Nendo: get(items, "Nendo"),
                Ki: get(items, "Ki"),
                value: get(items, "Uriagedaka"),
              }
            })
            const ToukiRieki = map(get(res, [1, "ResultList"]), items => {
              return {
                Nendo: get(items, "Nendo"),
                Ki: get(items, "Ki"),
                value: get(items, "ToukiRieki"),
              }
            })
            const BPS = map(get(res, [2, "ResultList"]), items => {
              return {
                Nendo: get(items, "Nendo"),
                Ki: get(items, "Ki"),
                value: get(items, "BPS"),
              }
            })
            const DPS = map(get(res, [3, "ResultList"]), items => {
              return {
                Nendo: get(items, "Nendo"),
                Ki: get(items, "Ki"),
                value: get(items, "DPS"),
              }
            })
            merge(_data, {
              GetCompanyInfo,
              Uriagedaka,
              ToukiRieki,
              BPS,
              DPS,
            })
          })
          return _data
        }
        const compareCompanyList = await compare.map(apis)
        await Promise.all(compareCompanyList).then(val => {
          setData(val)
        })
        store.dispatch(pageLoadRegist(loadingId, false))
      }
    })()
  }, [type])
  return data
}

// スクリーニング

export type ScreeningDetailType = {
  type: number
  to: string
  from: string
}

export type ScreeningActionType = {
  type:
    | "LOAD_SCREENING"
    | "LOADED_SCREENING"
    | "CHANGE_SCREENING_TOGGLE"
    | "LOAD_SCREENING_CONDITION"
    | "RESET_SCREENING_CONDITION"
    | "CHANGE_SCREENING_RECOMMEND"
    | "CHANGE_SCREENING_MARKET"
    | "CHANGE_SCREENING_COUNTRY"
    | "ALL_SCREENING_MARKET"
    | "CHANGE_SCREENING_JP_INDUSTRY"
    | "ALL_SCREENING_JP_INDUSTRY"
    | "CHANGE_SCREENING_SP_INDUSTRY"
    | "CHANGE_SCREENING_SP_CATEGORY"
    | "ALL_SCREENING_SP_INDUSTRY"
    | "CHANGE_SCREENING_DETAIL"
    | "RESET_SCREENING_DETAIL"
    | "SORT_SCREENING_DETAIL"
    | "CHANGE_SCREENING_COLUMN"
    | "CHANGE_SCREENING_PAGE"
  id?: number
  detail?: ScreeningDetailType
  columnType?: number
  // 以下一括用
  market?: Array<number>
  jpIndustry?: Array<number>
  spIndustry?: Array<number>
  details?: Array<ScreeningDetailType>
  sort?: string | null
}

export type ScreeningStateType = {
  load: boolean
  openToggle: Array<number>
  recommendFlag: number
  markets: Array<number>
  jpIndustry: Array<number>
  spIndustry: Array<number>
  detail: Array<ScreeningDetailType>
  columnType: number
  page: number
  sort: string | null
}

// anyなんとかする
export const useScreeningReducer = (
  state: ScreeningStateType,
  action: ScreeningActionType
) => {
  switch (action.type) {
    case "LOAD_SCREENING":
      return {
        ...state,
        load: true,
      }
    case "LOADED_SCREENING":
      return {
        ...state,
        load: false,
      }
    case "CHANGE_SCREENING_TOGGLE":
      return {
        ...state,
        openToggle: switchArrayType<number>(state.openToggle, action.id),
      }
    case "LOAD_SCREENING_CONDITION":
      return {
        ...state,
        recommendFlag: 0,
        markets: action.market,
        jpIndustry: action.jpIndustry,
        spIndustry: action.spIndustry,
        detail: action.details,
      }
    case "RESET_SCREENING_CONDITION":
      return {
        ...state,
        recommendFlag: null,
        markets: [],
        jpIndustry: [],
        spIndustry: [],
        detail: [],
      }
    case "CHANGE_SCREENING_RECOMMEND":
      return { ...state, recommendFlag: action.id, detail: action.details }
    case "CHANGE_SCREENING_MARKET":
      return {
        ...state,
        markets: switchArrayType<number>(state.markets, action.id),
      }
    case "CHANGE_SCREENING_COUNTRY":
      return {
        ...state,
        markets: switchArray<number>(state.markets, action.market),
      }
    case "ALL_SCREENING_MARKET":
      return {
        ...state,
        markets: state.markets.length === 0 ? action.market : [],
      }
    case "CHANGE_SCREENING_JP_INDUSTRY":
      return {
        ...state,
        jpIndustry: switchArrayType<number>(state.jpIndustry, action.id),
      }
    case "ALL_SCREENING_JP_INDUSTRY":
      return {
        ...state,
        jpIndustry: state.jpIndustry.length === 0 ? action.jpIndustry : [],
      }
    case "CHANGE_SCREENING_SP_INDUSTRY":
      return {
        ...state,
        spIndustry: switchArrayType<number>(state.spIndustry, action.id),
      }
    case "CHANGE_SCREENING_SP_CATEGORY":
      return {
        ...state,
        spIndustry: switchArray<number>(state.spIndustry, action.spIndustry),
      }
    case "ALL_SCREENING_SP_INDUSTRY":
      return {
        ...state,
        spIndustry: state.spIndustry.length === 0 ? action.spIndustry : [],
      }
    case "CHANGE_SCREENING_DETAIL":
      const hasValue = !action.detail.from && !action.detail.to
      let detailList = clone(state.detail)
      if (findIndex(state.detail, { type: action.detail.type }) >= 0) {
        if (!hasValue) {
          detailList = detailList.map(list => {
            return list.type === action.detail.type ? action.detail : list
          })
        } else {
          detailList = reject(detailList, list => {
            return list.type === action.detail.type
          })
        }
      } else {
        if (!hasValue) {
          detailList = concat(detailList, action.detail)
        } else {
          return state
        }
      }
      return {
        ...state,
        detail: detailList,
      }
    case "CHANGE_SCREENING_COLUMN":
      return { ...state, columnType: action.columnType }
    case "CHANGE_SCREENING_PAGE":
      return { ...state, page: action.id, load: true }
    case "SORT_SCREENING_DETAIL":
      return {
        ...state,
        load: true,
        sort: action.sort,
      }
  }
}

type UseScreeningResultType = (
  state: ScreeningStateType,
  cb: Function
) => APIResponse.Res
export const useScreeningResult: UseScreeningResultType = (state, cb) => {
  const loadingId = useMemo(() => uuid(), [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const path = get(useLocation(), "pathname")

  const [data, setData] = useState<APIResponse.Res>()

  const load = async () => {
    await store.dispatch(pageLoadRegist(loadingId, true))
    const params: RequestParams = {
      _skip: PAGE_NUM.PAGE_20 * state.page,
      _take: PAGE_NUM.PAGE_20,
      _orderBy: state.sort,
    }
    if (!!state.markets.length)
      params["Markets"] = createRequestBit(state.markets, 21)
    if (!!state.jpIndustry.length)
      params["TPSectors"] = createRequestBit(state.jpIndustry, 33)
    if (!!state.spIndustry.length)
      params["SPSectors"] = createRequestBit(state.spIndustry, 69)
    if (!!state.detail.length)
      params["Filter"] = state.detail.map(data => ({
        Type: data.type,
        Param: { From: data.from, To: data.to },
      }))
    const data = await APIs.Tool.Screening(params)
    if (!get(data, "Result")) setData(data)
    await store.dispatch(pageLoadRegist(loadingId, false))
    cb()
  }
  useEffect(() => {
    if (state.load) load()
  }, [state.load])

  useEffect(() => {
    if (isPageLoading && includes(path, "screening")) load()
  }, [isPageLoading])
  return data
}

// 株主優待

export type useBenefitActionType = {
  type:
    | "CHANGE_DISPLAY_TOGGLE"
    | typeof STOCK_BENEFIT_CONDITIONS.month.type
    | typeof STOCK_BENEFIT_CONDITIONS.category.type
    | typeof STOCK_BENEFIT_CONDITIONS.price.type
    | "CHANGE_BENEFIT_PAGE"
    | "CHANGE_BENEFIT_SORT"
    | "LOAD_BENEFIT"
  value?: number
  page?: number
  toggleId?: number
  sort?: [string, boolean]
}

export type useBenefitStateType = {
  month: Array<number>
  category: Array<number>
  price: Array<number>
  page: number
  sort: [string, boolean]
  displayToggle: Array<number>
  load: boolean
}

export const useBenefitReducer = (
  state: useBenefitStateType,
  action: useBenefitActionType
) => {
  switch (action.type) {
    case "CHANGE_DISPLAY_TOGGLE":
      const displayToggle = switchArrayType(
        state.displayToggle,
        action.toggleId
      )
      return { ...state, displayToggle }
    case "CHANGE_BENEFIT_MONTH":
      return {
        ...state,
        month: switchArrayType<number>(state.month, action.value),
      }
    case "CHANGE_BENEFIT_CATEGORY":
      return {
        ...state,
        category: switchArrayType<number>(state.category, action.value),
      }
    case "CHANGE_BENEFIT_PRICE":
      return {
        ...state,
        price: switchArrayType<number>(state.price, action.value),
      }
    case "CHANGE_BENEFIT_PAGE":
      return { ...state, page: action.page, load: true }
    case "LOAD_BENEFIT":
      return { ...state, load: !state.load }
    case "CHANGE_BENEFIT_SORT":
      return state
  }
}

type UseStockBenefitResult = (
  state: useBenefitStateType,
  cb: Function
) => APIResponse.Res
export const useStockBenefitResult: UseStockBenefitResult = (state, cb) => {
  const loadingId = useMemo(() => uuid(), [])
  const isPageLoading = useSelector(
    (state: { PageLoad: LoadStateType }) => state.PageLoad.load
  )
  const [data, setData] = useState<APIResponse.Res>()
  const path = get(useLocation(), "pathname")
  const [loadingFlag, setLoadingFlag] = useState(false)

  const load = async () => {
    await store.dispatch(pageLoadRegist(loadingId, true))
    const params: StockBenefitRequestParams = {
      _take: PAGE_NUM.PAGE_20,
      _skip: PAGE_NUM.PAGE_20 * state.page,
    }
    if (!!state.month.length)
      params.RightFixMonth = getRequestParameter(
        state.month,
        STOCK_BENEFIT_CONDITIONS.month.list,
        "value"
      )
    if (!!state.price.length)
      params.NeededAmount = getRequestParameter(
        state.price,
        STOCK_BENEFIT_CONDITIONS.price.list,
        "param"
      )
    if (!!state.category.length) {
      const categoryList = getRequestParameter(
        state.category,
        STOCK_BENEFIT_CONDITIONS.category.list,
        "value"
      )
      const stringList = categoryList.map(val => `SubCategory='${val}'`)
      params._where = stringList.join(" OR ")
    }
    const data = await APIs.Tool.StockholderBenefit(params)
    setData(data)
    await store.dispatch(pageLoadRegist(loadingId, false))
    setTimeout(() => {
      setLoadingFlag(false)
    }, 1000)
    cb()
  }
  useEffect(() => {
    if (!state.load || loadingFlag) return
    load()
  }, [state.load])

  useEffect(() => {
    if (isPageLoading && includes(path, "stockholder")) {
      setLoadingFlag(true)
      load()
    }
  }, [isPageLoading])
  return data
}
